/*
 * Filename: component-field.tsx
 * Description: component for component field.
 */

import React, { BaseSyntheticEvent } from 'react';
import { Field } from 'formik';
import { CQMasterDataStore } from '../../../store/cq-master-data.store';
import { observer } from 'mobx-react';
import { CQAppConstant } from '../../../app-constants';
import { CQAppStore } from '../../../store/cq-app-store';
import IconPicker from '../../icon-picker/icon-picker';
import TemplateConfigurationController from "../template-config.controller";
import { CQBoolean, CQActionOverride, CQBaseClassName, CQPicklist } from '../field-type-component';

export interface IFieldData {
    name: string; 
    description: string; 
    required: boolean; 
    inputParamKey: string
    value?: any[];
    type: string; 
    defaultValue?: string; 
    disabled?: boolean;
    isComponentProperty: boolean;
    size?: number;
}

const FieldComponent = (props: any) => {
    let { name, description, required, inputParamKey, type, defaultValue, disabled }: any = props.fieldData;
    const templateConfigCtrl: TemplateConfigurationController = new TemplateConfigurationController();
    const templateInfo = CQMasterDataStore.OutputConfig.getStoreData(props.selectedTemplateName);
    const objectName = templateInfo.selectedObject;
    let fieldValue = templateInfo[inputParamKey];
    if(!fieldValue && defaultValue){
        fieldValue = defaultValue;
        CQMasterDataStore.OutputConfig.updateTemplatePropertyValue(props.selectedTemplateName, props.fieldData.inputParamKey, fieldValue);
    }

    let componentTypeValue: string = "";
    if(props.selectedTemplateName === "cqEsig100"){
        componentTypeValue = templateInfo["componentType"];
    }

    let recordTypesValues: any[] = [];
    if(props.selectedTemplateName === "cqForm100"){
        recordTypesValues = templateInfo.layout.recordTypes || [];
    }

    const orgNameSpace = templateConfigCtrl.getCookieValue(CQAppConstant.LOGGED_IN_ORG_NAME_SPACE);                        
    
    const defaultValueChange = async (e: BaseSyntheticEvent) => {
        await props.handleChange(e);
        props.updateComponent(e.target.name);
        if (props.fieldData.inputParamKey === 'prefixName') {
            templateConfigCtrl.setPrefixName(props.templateName, false, e.target.value);
        }
    }

    const renderViews = () => {
        try {
            switch (inputParamKey) {
                case CQAppConstant.CLASS_NAME:

                    return (
                        <CQBaseClassName
                            key= {inputParamKey} 
                            fieldData= {props.fieldData}
                            templateName= {props.selectedTemplateName}
                        />
                    );

                case CQAppConstant.ACTION_OVERRIDE:
                    
                    return (
                        <CQActionOverride 
                            key= {inputParamKey} 
                            fieldData= {props.fieldData}
                            templateName= {props.selectedTemplateName}
                            updateComponent= {(value) => props.updateComponent(value)}
                        />
                    );
                case CQAppConstant.ICON_NAME:

                    return (
                        <IconPicker
                            key= {inputParamKey} 
                            name={props.fieldData.inputParamKey}
                            value={templateInfo[inputParamKey]}
                            onChange={props.handleChange}
                            updateIconName={props.updateIconName}
                        />
                    );
                default:
                    /**
                         * Returns the checkbox field if the type received input param is 'boolean'.
                         * Returns the text field as default return for other type than 'boolean'.
                    */
                    {
                        if (type === CQAppConstant.BOOLEAN) {
                            return (
                                <CQBoolean 
                                    key= {inputParamKey} 
                                    fieldData= {props.fieldData}
                                    templateName= {props.selectedTemplateName}
                                />
                            );
                        }else if(type === 'picklist'){                            
                            return (
                                <CQPicklist
                                    key= {inputParamKey}  
                                    action= {{
                                        generateComponentName: props.updateComponent,
                                        resetAndDisableSelectionPanel: props.resetAndDisableSelectionPanel
                                    }}
                                    fieldData= {props.fieldData}
                                    templateName= {props.selectedTemplateName}
                                />
                            );
                        }
                        else {
                            return (
                                <Field
                                    key= {inputParamKey}  
                                    value={templateInfo[inputParamKey]}
                                    type="text" 
                                    id={inputParamKey} 
                                    placeholder={`Please enter text for ${inputParamKey}`} 
                                    className="slds-input" 
                                    name={props.fieldData.inputParamKey}
                                    onChange={defaultValueChange} 
                                    required={!orgNameSpace && inputParamKey ==="prefixName"}
                                    disabled={
                                        (inputParamKey === "componentName" && [CQAppConstant.COMPONENT_TYPE_FORM, CQAppConstant.COMPONENT_TYPE_ESIG_ONLY].includes(componentTypeValue)) || disabled || false
                                    }
                                />
                            );
                        }
                    }
            }
        }
        catch (error : any) {

            CQAppStore.loader.setVisibility(false);
            const errorObj = {
                errorType: CQAppConstant.ERROR_CUSTOM,
                errorMessage: error.error ? error.error : error.message,
                errorStatus: error.status,
                componentName: ''
            };
            CQAppStore.error.dispatchError(errorObj);
        }
    };

    if(
        props.selectedTemplateName === "cqEsig100" &&
        (componentTypeValue === CQAppConstant.COMPONENT_TYPE_LIGHTNING_COMPONENT) && ["parentField"].includes(inputParamKey)
    ){
        return null;
    }else if (
        props.selectedTemplateName === "cqEsig100" &&
        (componentTypeValue !== CQAppConstant.COMPONENT_TYPE_FORM && componentTypeValue !== CQAppConstant.COMPONENT_TYPE_LIGHTNING_COMPONENT) && ["parentField", "relatedField"].includes(inputParamKey)
    ) {
        return null;
    }else if(props.selectedTemplateName === "cqForm100" && inputParamKey === "recordTypeDeveloperName" && (recordTypesValues.length === 0 || !templateInfo["actionOverride"])){
        return null;
    }else if(inputParamKey === "componentNamespace" && !templateConfigCtrl.getCookieValue(CQAppConstant.LOGGED_IN_ORG_NAME_SPACE)){
        return null;
    }else {
        return (
            <div key={inputParamKey} className="slds-form-element">
                <label className="slds-form-element__label">
                    {(required || (!orgNameSpace && inputParamKey==='prefixName'))&& (
                        <abbr className="slds-required" title="required">
                            *{" "}
                        </abbr>
                    )}
                    {name}
                </label>
                <p>{description}</p>
                <div className="slds-form-element__control">{renderViews()}</div>
            </div>
        );
    }
};

export const ComponentFieldComponent = observer(FieldComponent);
